import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  Star, 
  Check, 
  AlertCircle, 
  ShoppingBag, 
  Ruler, 
  ShieldCheck, 
  RotateCcw, 
  MessageSquarePlus, 
  Heart
} from 'lucide-react';
import { useLojaCart } from '../../context/LojaCartContext';
import LojaService from '../../services/Loja/LojaService';

export default function ProductModal({ product, isOpen, onClose }) {
  const { addToCart, toggleFavorite, isFavorite } = useLojaCart();

  const [activeImage, setActiveImage] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedGender, setSelectedGender] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('especificacoes'); // 'especificacoes' | 'medidas' | 'trocas' | 'avaliacoes'
  
  // Review form
  const [reviewName, setReviewName] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  // Inicializa quando o produto abre
  useEffect(() => {
    if (product) {
      const defaultImg = (product.images && product.images.length > 0)
        ? (product.images.find(i => i.is_primary)?.url || product.images[0].url || product.images[0])
        : '/images/produtos/camisa-urb.png';
      setActiveImage(defaultImg);

      if (product.variants && product.variants.length > 0) {
        // Seleciona primeira variante em estoque
        const available = product.variants.find(v => v.stock > 0) || product.variants[0];
        setSelectedSize(available.size || '');
        setSelectedColor(available.color || '');
        setSelectedGender(available.gender || product.gender || 'Unissex');
      } else {
        setSelectedSize('Único');
        setSelectedColor('Padrão');
        setSelectedGender(product.gender || 'Unissex');
      }

      setQuantity(1);
      setActiveTab('especificacoes');
      setReviewSubmitted(false);
    }
  }, [product]);

  // Variações únicas disponíveis
  const availableSizes = useMemo(() => {
    if (!product?.variants) return [];
    return Array.from(new Set(product.variants.map(v => v.size))).filter(Boolean);
  }, [product]);

  const availableColors = useMemo(() => {
    if (!product?.variants) return [];
    const colorMap = new Map();
    product.variants.forEach(v => {
      if (v.color && !colorMap.has(v.color)) {
        colorMap.set(v.color, v.color_hex || '#0d120f');
      }
    });
    return Array.from(colorMap.entries()).map(([name, hex]) => ({ name, hex }));
  }, [product]);

  const availableGenders = useMemo(() => {
    if (!product?.variants) return [];
    const genders = Array.from(new Set(product.variants.map(v => v.gender))).filter(Boolean);
    return genders.length > 1 ? genders : [];
  }, [product]);

  // Variante selecionada atual
  const currentVariant = useMemo(() => {
    if (!product?.variants || product.variants.length === 0) {
      return { size: 'Único', color: 'Padrão', stock: 10, sku: product?.sku };
    }

    return product.variants.find(v => {
      const matchSize = v.size === selectedSize;
      const matchColor = v.color === selectedColor;
      const matchGender = availableGenders.length > 0 ? v.gender === selectedGender : true;
      return matchSize && matchColor && matchGender;
    }) || null;
  }, [product, selectedSize, selectedColor, selectedGender, availableGenders]);

  if (!isOpen || !product) return null;

  const isFav = isFavorite(product.id);
  const price = product.price;
  const promoPrice = product.promotional_price;
  const unitPrice = promoPrice || price;
  const hasDiscount = promoPrice && promoPrice < price;

  const currentStock = currentVariant ? currentVariant.stock : 0;
  const isOutOfStock = currentStock <= 0;

  const handleAddToCart = () => {
    if (isOutOfStock || !currentVariant) return;
    addToCart(product, currentVariant, quantity);
    onClose();
  };

  const handleSendReview = async (e) => {
    e.preventDefault();
    if (!reviewComment.trim()) return;
    await LojaService.addReview(product.id, {
      name: reviewName || 'Atleta URBRACE',
      rating: reviewRating,
      comment: reviewComment
    });
    setReviewSubmitted(true);
    setReviewComment('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      
      {/* Modal Container */}
      <div 
        className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-urb-dark rounded-3xl border border-urb-border shadow-2xl flex flex-col no-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Botão Fechar */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-urb-card-light/80 hover:bg-urb-primary hover:text-black text-white flex items-center justify-center z-20 transition-all border border-urb-border cursor-pointer"
          aria-label="Fechar modal"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-6 sm:p-8">
          
          {/* Coluna Esquerda: Galeria de Imagens */}
          <div className="md:col-span-6 flex flex-col gap-4">
            
            {/* Imagem Principal */}
            <div className="relative aspect-square w-full rounded-2xl bg-gradient-to-b from-urb-card-light/40 to-urb-dark p-6 flex items-center justify-center border border-urb-border/80 overflow-hidden">
              
              {hasDiscount && (
                <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-md bg-urb-primary text-black font-black text-xs uppercase tracking-wider">
                  OFERTA ESPECIAL
                </div>
              )}

              <button
                type="button"
                onClick={() => toggleFavorite(product.id)}
                className={`absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-md transition-all z-10 cursor-pointer ${
                  isFav
                    ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                    : 'bg-urb-dark/80 text-urb-text-muted hover:text-white border border-urb-border'
                }`}
              >
                <Heart className={`w-4 h-4 ${isFav ? 'fill-red-400' : ''}`} />
              </button>

              <img
                src={activeImage}
                alt={product.name}
                className="w-full h-full object-contain drop-shadow-[0_15px_25px_rgba(0,0,0,0.8)] transition-all duration-300"
                onError={(e) => {
                  if (!e.target.src.includes('/loja/')) {
                    e.target.src = `/loja${activeImage}`;
                  }
                }}
              />
            </div>

            {/* Miniaturas da Galeria */}
            {product.images && product.images.length > 1 && (
              <div className="flex items-center gap-3 overflow-x-auto pb-1 no-scrollbar">
                {product.images.map((img, idx) => {
                  const url = img.url || img;
                  const isSel = activeImage === url;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImage(url)}
                      className={`w-18 h-18 rounded-xl p-2 bg-urb-card border transition-all shrink-0 cursor-pointer ${
                        isSel
                          ? 'border-urb-primary ring-2 ring-urb-primary/30'
                          : 'border-urb-border opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={url} alt={`Thumb ${idx}`} className="w-full h-full object-contain" />
                    </button>
                  );
                })}
              </div>
            )}

            {/* Selos de Benefício */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="flex items-center gap-2 p-2.5 rounded-xl bg-urb-card/60 border border-urb-border/50 text-xs text-urb-text-secondary">
                <RotateCcw className="w-4 h-4 text-urb-primary shrink-0" />
                <span>1ª Troca Grátis 30 dias</span>
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-xl bg-urb-card/60 border border-urb-border/50 text-xs text-urb-text-secondary">
                <ShieldCheck className="w-4 h-4 text-urb-primary shrink-0" />
                <span>Original URBRACE</span>
              </div>
            </div>

          </div>

          {/* Coluna Direita: Informações & Seletor de Variantes */}
          <div className="md:col-span-6 flex flex-col justify-between space-y-6">
            
            <div className="space-y-4">
              
              {/* Categoria e Avaliações */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-widest text-urb-primary">
                  {product.category_id ? product.category_id.replace('cat_', '') : 'EQUIPAMENTO'}
                </span>
                <div className="flex items-center gap-1.5 text-xs text-yellow-400 font-bold">
                  <Star className="w-4 h-4 fill-yellow-400" />
                  <span>{product.rating_avg || '4.9'}</span>
                  <span className="text-urb-text-muted">({product.rating_count || 38} avaliações)</span>
                </div>
              </div>

              {/* Título do Produto */}
              <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight leading-snug">
                {product.name}
              </h2>

              {/* Preço Oficial */}
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-black text-white">
                  R$ {unitPrice.toFixed(2).replace('.', ',')}
                </span>
                {hasDiscount && (
                  <span className="text-base text-urb-text-muted line-through">
                    R$ {price.toFixed(2).replace('.', ',')}
                  </span>
                )}
                <span className="text-xs text-urb-text-secondary">
                  em até 6x de R$ {(unitPrice / 6).toFixed(2).replace('.', ',')} sem juros
                </span>
              </div>

              {/* Seletor de Gênero (caso exista) */}
              {availableGenders.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-black uppercase text-urb-text-muted tracking-wider block">
                    Gênero: <strong className="text-white">{selectedGender}</strong>
                  </span>
                  <div className="flex gap-2">
                    {availableGenders.map(g => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setSelectedGender(g)}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          selectedGender === g
                            ? 'bg-urb-primary text-black font-black'
                            : 'bg-urb-card-light border border-urb-border text-urb-text-secondary hover:text-white'
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Seletor de Tamanho */}
              {availableSizes.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black uppercase text-urb-text-muted tracking-wider">
                      Tamanho: <strong className="text-white">{selectedSize}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveTab('medidas')}
                      className="inline-flex items-center gap-1 text-[11px] text-urb-primary hover:underline font-bold cursor-pointer"
                    >
                      <Ruler className="w-3 h-3" />
                      <span>Guia de Medidas</span>
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableSizes.map(size => {
                      const isSel = selectedSize === size;
                      return (
                        <button
                          key={size}
                          type="button"
                          onClick={() => setSelectedSize(size)}
                          className={`w-12 h-10 rounded-xl text-xs font-black flex items-center justify-center transition-all cursor-pointer ${
                            isSel
                              ? 'bg-urb-primary text-black shadow-[0_0_12px_rgba(138,230,0,0.3)]'
                              : 'bg-urb-card-light border border-urb-border text-white hover:border-urb-primary/50'
                          }`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Seletor de Cor */}
              {availableColors.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-black uppercase text-urb-text-muted tracking-wider block">
                    Cor: <strong className="text-white">{selectedColor}</strong>
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {availableColors.map(color => {
                      const isSel = selectedColor === color.name;
                      return (
                        <button
                          key={color.name}
                          type="button"
                          onClick={() => setSelectedColor(color.name)}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                            isSel
                              ? 'bg-urb-card-light border-2 border-urb-primary text-white'
                              : 'bg-urb-card border border-urb-border text-urb-text-secondary hover:text-white'
                          }`}
                        >
                          <span
                            className="w-3 h-3 rounded-full border border-white/30"
                            style={{ backgroundColor: color.hex }}
                          />
                          <span>{color.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Feedback de Estoque em Tempo Real */}
              <div className="pt-2">
                {isOutOfStock ? (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-bold">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>Esgotado nesta variação. Escolha outro tamanho ou cor.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs font-semibold text-urb-primary">
                    <Check className="w-4 h-4" />
                    <span>Em estoque ({currentStock} unidades disponíveis para envio imediato)</span>
                  </div>
                )}
              </div>

            </div>

            {/* Ações: Seletor de Quantidade + Botão de Compra */}
            <div className="space-y-3 pt-4 border-t border-urb-border">
              <div className="flex items-center gap-3">
                
                {/* Contador de Quantidade */}
                <div className="flex items-center bg-urb-card-light rounded-xl border border-urb-border p-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    disabled={quantity <= 1 || isOutOfStock}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-30 cursor-pointer"
                  >
                    -
                  </button>
                  <span className="w-10 text-center font-bold text-sm text-white">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity(q => Math.min(currentStock, q + 1))}
                    disabled={quantity >= currentStock || isOutOfStock}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-30 cursor-pointer"
                  >
                    +
                  </button>
                </div>

                {/* Botão Adicionar ao Carrinho */}
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                  className="flex-1 py-4 px-6 rounded-xl font-black text-sm uppercase tracking-wider flex items-center justify-center gap-2.5 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed bg-urb-primary text-black hover:bg-urb-primary-hover shadow-[0_0_20px_rgba(138,230,0,0.25)]"
                >
                  <ShoppingBag className="w-4 h-4 stroke-[2.5]" />
                  <span>{isOutOfStock ? 'ESGOTADO' : 'ADICIONAR AO CARRINHO'}</span>
                </button>

              </div>
            </div>

          </div>

        </div>

        {/* Abas de Detalhes Técnicos, Medidas e Avaliações */}
        <div className="border-t border-urb-border bg-urb-dark/95 p-6 sm:p-8 space-y-6">
          
          {/* Navegação das Abas */}
          <div className="flex items-center gap-2 border-b border-urb-border pb-3 overflow-x-auto no-scrollbar">
            {[
              { id: 'especificacoes', label: 'Ficha Técnica' },
              { id: 'medidas', label: 'Tabela de Medidas' },
              { id: 'trocas', label: 'Política de Troca' },
              { id: 'avaliacoes', label: `Avaliações (${product.rating_count || 38})` }
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold uppercase tracking-wider transition-all cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-urb-primary/10 text-urb-primary border border-urb-primary/30'
                    : 'text-urb-text-secondary hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Conteúdo da Aba */}
          <div>
            {activeTab === 'especificacoes' && (
              <div className="space-y-4 text-sm text-urb-text-secondary leading-relaxed">
                <p className="text-white font-medium">{product.description}</p>
                <div className="p-4 rounded-xl bg-urb-card border border-urb-border space-y-2">
                  <h4 className="text-xs font-black uppercase text-urb-primary tracking-wider">
                    Destaques de Performance
                  </h4>
                  <p className="text-xs text-urb-text-secondary">
                    {product.technical_info || 'Composição esportiva ultraleve com tecnologia respirável e tratamento bactericida com íons de prata.'}
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'medidas' && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-urb-card border border-urb-border">
                  <h4 className="text-xs font-black uppercase text-urb-primary tracking-wider mb-2">
                    Guia Anatômico Oficial URBRACE
                  </h4>
                  <pre className="whitespace-pre-wrap font-sans text-xs text-urb-text-secondary leading-relaxed">
                    {product.size_guide || 'P: Tórax 92cm x Altura 68cm\nM: Tórax 98cm x Altura 71cm\nG: Tórax 106cm x Altura 74cm\nGG: Tórax 114cm x Altura 77cm'}
                  </pre>
                </div>
                <p className="text-[11px] text-urb-text-muted">
                  * Dica: meça uma camisa que você já usa e compare com a tabela antes de fechar o pedido.
                </p>
              </div>
            )}

            {activeTab === 'trocas' && (
              <div className="space-y-3 text-xs text-urb-text-secondary leading-relaxed">
                <div className="p-4 rounded-xl bg-urb-card border border-urb-border space-y-2">
                  <h4 className="text-xs font-black uppercase text-urb-primary tracking-wider">
                    Garantia de Satisfação e 1ª Troca Grátis
                  </h4>
                  <p>
                    Na URBRACE, a sua primeira troca de tamanho ou modelo é 100% gratuita. 
                    Você tem até 30 dias corridos após o recebimento para solicitar a troca do seu equipamento.
                  </p>
                  <p>
                    O produto deve estar sem marcas de uso, na embalagem original e com as etiquetas afixadas.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'avaliacoes' && (
              <div className="space-y-6">
                
                {/* Média de Avaliação */}
                <div className="flex items-center gap-4 p-4 rounded-xl bg-urb-card border border-urb-border">
                  <div className="text-center">
                    <span className="text-3xl font-black text-white">{product.rating_avg || '4.9'}</span>
                    <div className="flex text-yellow-400 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <div className="border-l border-urb-border pl-4 text-xs text-urb-text-secondary">
                    <strong className="text-white block font-bold">Aprovado por atletas de alta performance</strong>
                    98% dos corredores recomendam esta peça para treinos diários e provas de longa distância.
                  </div>
                </div>

                {/* Formulário de Envio de Nova Avaliação */}
                <form onSubmit={handleSendReview} className="p-4 rounded-xl bg-urb-card border border-urb-border space-y-3">
                  <h4 className="text-xs font-black uppercase text-urb-primary tracking-wider flex items-center gap-1.5">
                    <MessageSquarePlus className="w-4 h-4" />
                    <span>Avaliar este produto</span>
                  </h4>
                  
                  {reviewSubmitted ? (
                    <div className="p-3 rounded-lg bg-urb-primary/10 border border-urb-primary/30 text-urb-primary text-xs font-bold">
                      Obrigado pelo seu feedback! Sua avaliação foi registrada com sucesso.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={reviewName}
                          onChange={(e) => setReviewName(e.target.value)}
                          placeholder="Seu nome ou apelido de corredor"
                          className="px-3 py-2 rounded-lg bg-urb-dark border border-urb-border text-xs text-white placeholder-urb-text-muted focus:outline-none focus:border-urb-primary"
                        />
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-urb-dark border border-urb-border text-xs text-white">
                          <span className="text-urb-text-muted">Nota:</span>
                          <div className="flex gap-1 cursor-pointer">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star
                                key={n}
                                onClick={() => setReviewRating(n)}
                                className={`w-4 h-4 cursor-pointer ${
                                  n <= reviewRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      <textarea
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        placeholder="Conte como foi sua experiência com o caimento, tecido e conforto..."
                        rows={2}
                        required
                        className="w-full px-3 py-2 rounded-lg bg-urb-dark border border-urb-border text-xs text-white placeholder-urb-text-muted focus:outline-none focus:border-urb-primary"
                      />

                      <button
                        type="submit"
                        className="px-4 py-2 rounded-lg bg-urb-primary text-black font-bold text-xs uppercase tracking-wider hover:bg-urb-primary-hover cursor-pointer"
                      >
                        Enviar Avaliação
                      </button>
                    </>
                  )}
                </form>

              </div>
            )}
          </div>

        </div>

      </div>

    </div>
  );
}
